<script setup>
import { computed } from 'vue'
import { Icon } from '@iconify/vue'

/**
 * AppBadge — colored badge for status / tags / counts.
 *
 * Sizes (the pill shapes in the codebase):
 *   xs — px-2 py-0.5 rounded-md   (22px tall — the tag chip and the inline
 *        "4 available" counters; the tightest pill the design uses)
 *   sm — px-2 py-1.5 rounded-[6px]  (Orders status pill, in-table use)
 *   md — px-2 py-[6px] rounded-md (Notifications / ProgressJobs / Listings pill)
 *
 * Variants → bg + text token pairs. Use `customClass` for one-off palettes.
 *
 * Legacy `.badge` CSS class stays in _tailwind.css for backward compat —
 * new code should use this component.
 */
const props = defineProps({
  variant: { type: String, default: 'gray' },
  // 'gray' | 'muted' | 'yellow' | 'blue' | 'green' | 'red' | 'pink' | 'orange' | 'purple' | 'indigo' | 'tint' | 'tint-light' | 'tint-faint'
  size:    { type: String, default: 'sm' },     // 'xs' | 'sm' | 'md'
  icon:    { type: String, default: null },     // any Iconify icon name
  customClass: { type: String, default: '' },   // overrides bg+text when variant doesn't fit
  pill:    { type: Boolean, default: false },   // round-ended py-0.5 pill instead of the size's shape
})

/*
 * Shades come from the status-badge component in Figma (node 23574:416364).
 *
 * Grounds: the **100** step on the tinted variants, **50** for purple/indigo,
 * gray/100. Text — and icons, which inherit `currentColor` — sit at **900**, per
 * the designer's note that all badge text and icons were darkened to that tone.
 *
 * The colour NAMES already mean the right thing: `flowbite/plugin` supplies this
 * app's palette, which is the same scale the design file is drawn in, so
 * `bg-green-100` really is #DEF7EC. Nothing extra is needed in
 * `tailwind.config.js` to match the design here.
 *
 * Caveat worth keeping: the node's bound variables still expose the 800 step
 * rather than 900 for these families. The designer's note is the newer signal
 * and is what shipped; if a future token pull still shows 800, confirm with
 * design before "fixing" this back.
 *
 * Two variants keep their own shade on purpose:
 *   `muted` is meant to recede (the design draws "Not for Sale" as gray/50 on
 *   gray/400), so darkening it would defeat the one thing it is for;
 *   `tint` is the brand blue, which names itself and isn't a status.
 */
const VARIANTS = {
  gray:   'bg-gray-100   text-gray-900',
  muted:  'bg-gray-50    text-gray-400',
  yellow: 'bg-yellow-100 text-yellow-900',
  blue:   'bg-blue-100   text-blue-900',
  green:  'bg-green-100  text-green-900',
  red:    'bg-red-100    text-red-900',
  pink:   'bg-pink-50    text-pink-900',
  orange: 'bg-orange-100 text-orange-900',
  // Both come from the Figma status set: purple is "Not for Sale", indigo the
  // second "Ready to Ship". 50 grounds, not 100 — that's what the design gives
  // these two, unlike the tinted variants above.
  purple: 'bg-purple-50  text-purple-900',
  indigo: 'bg-indigo-50  text-indigo-900',
  tint:   'bg-tint-1 bg-opacity-20 text-tint-1',
  // The onboarding cards' lock badge and step count: a blue-100 or blue-50 ground under the brand blue.
  'tint-light': 'bg-blue-100 text-tint-1',
  'tint-faint': 'bg-blue-50  text-tint-1',
}

const SIZES = {
  xs: 'px-2 py-0.5 rounded-md',
  sm: 'px-2 py-1.5 rounded-[6px]',
  md: 'px-2 py-[6px] rounded-md',
}

const PILL = 'px-2 py-0.5 rounded-full'

const variantClass = computed(() => props.customClass || VARIANTS[props.variant] || VARIANTS.gray)
const sizeClass    = computed(() => props.pill ? PILL : (SIZES[props.size] || SIZES.sm))
</script>

<template>
  <span
    class="inline-flex items-center gap-x-1 text-xs font-medium"
    :class="[variantClass, sizeClass]"
  >
    <Icon v-if="icon" :icon="icon" class="text-sm shrink-0" />
    <slot />
  </span>
</template>
